import { useState, useEffect, KeyboardEvent, useRef } from "react";
import { ArrowRightCircleIcon } from '@heroicons/react/24/solid';

import { useNavigate } from "@remix-run/react";
import axios from "axios";
import '../../styles/app.css';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUpRightFromSquare, faLightbulb, faSave, faSliders } from "@fortawesome/free-solid-svg-icons";
import { ScreenerIdeasOffcanvas } from "../../components/offcanvas/ScreenerIdeasOffcanvas";
import { FiltersOffcanvas } from "../../components/offcanvas/FiltersOffcanvas";
import { useDebounce } from '../../hooks/useDebounce';
import Button from "../../components/Button";

interface ScreenerFilter {
    name: string;
    key: string;
    shortDescription: string;
}

interface ScreenerSectionProps {
    filterList: FilterItem[];
    isHomePage: boolean;
    handleFilterDataApi: (queryArr: string[]) => void;
    onScreenerQueryAppend: (queryArr: string[]) => void;
    isDesktop: boolean;
    isActive: boolean;
    isInputFocused: boolean;
    displayArray: string[];
    onModalClose: () => void;
    companyScreenerApi: (queryArr: string[], limit: number, offset: number, sort_by: string, sort_direction: string) => void;
    resetCurrentPage: () => void;
    isAuthenticated: boolean;
    userId: string;
    placeholder: string;
}

export interface FilterItem {
    name: string;
    key: string;
    isText: boolean;
    operatorSuggestions: string[];
    shortDescription: string;
    type: string;
    is_amount_filter: boolean;
    values: { name: string; value: string }[];
    filter_type?: string;
    example_text?: string;
    glossary_key?: string;
}

// Add interface for column definition
interface DataTableColumn {
    name: string;
    selector: (row: any) => any;
    sortable: boolean;
    sortFunction?: (a: any, b: any) => number;
    cell?: (row: any) => JSX.Element | string;
}

const ModalTextAreaFunc: React.FC<ScreenerSectionProps> = ({ filterList, isHomePage, handleFilterDataApi, onScreenerQueryAppend, isDesktop, isActive, isInputFocused, displayArray, onModalClose, companyScreenerApi, resetCurrentPage, isAuthenticated, userId, placeholder }) => {

    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState('');
    const [isScreenerIdeasOpen, setScreenerIdeasOpen] = useState(false);
    const [isFiltersOpen, setFiltersOpen] = useState(false);
    const [selectedFilter, setSelectedFilter] = useState<ScreenerFilter | null>(null);
    const [selectedIndex, setSelectedIndex] = useState<number>(-1);
    const [activeSuggestion, setActiveSuggestion] = useState<number>(0);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const suggestionsRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const [filteredSuggestions, setFilteredSuggestions] = useState<FilterItem[]>(filterList);
    const [currentStep, setCurrentStep] = useState(1);
    const [queryArr, setQueryArr] = useState<string[]>([]);
    const [displayArr, setDisplayArr] = useState<string[]>(displayArray || []);
    const [queryOriginalArr, setQueryOriginalArr] = useState<string[]>([]);
    const [searchTerm, setsearchTerm] = useState('');
    const [activeObjArr, setActiveObjArr] = useState<any[]>([]);
    const [operatorArray, setOperatorArray] = useState<any[]>([]);
    const [valuesArray, setValuesArray] = useState<any[]>([]);
    const [textValue, setTextValue] = useState("");
    const [inputTextValue, setInputTextValue] = useState("");
    const [bigIntFilters, setBigIntFilters] = useState([
        "free_cash_flow_avg_4q",
        "free_cash_flow_avg_6q",
        "revenue_last_quarter",
        "net_income_last_quarter",
        "gross_profit_last_quarter",
        "revenue_ttm",
        "net_income_ttm",
        "gross_profit_ttm",
        "market_cap",
        "total_assets",
        "total_current_assets",
        "total_non_current_assets",
        "total_liabilities",
        "total_current_liabilities",
        "total_non_current_liabilities",
        "total_equity",
        "enterprise_value_ttm",
        "ebitda"
    ]);
    const [currentPage, setCurrentPage] = useState(1);
    const [columnHeader, setColumnHeader] = useState<string[]>([]);
    const [totalPages, setTotalPages] = useState(0);
    const [headerObj, setHeaderObj] = useState<DataTableColumn[]>([]);
    const [columnBodyRows, setColumnBodyRows] = useState<string[]>([]);
    const [filteredData, setFilteredData] = useState<any[]>([]);
    const [filteredKeys, setFilteredKeys] = useState<string[]>([]);
    const [keyMap, setkeyMap] = useState<any>({});
    const [tickerListData, setTickerListData] = useState<any[]>([]);
    const [showSaveFilterModal, setShowSaveFilterModal] = useState(false);
    const [activeEducationElementId, setActiveEducationElementId] = useState<string | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isMobileFocused, setIsMobileFocused] = useState(false);
    const [isSignInModalOpen, setIsSignInModalOpen] = useState(false);
    const [showTextAreaModal, setShowTextAreaModal] = useState(false);

    let userType = 'gold';
    const itemsPerPage = 10;

    // Initialize queryArr from displayArray
    // Only update displayArr from queryArr if we have queryArr items
    const debouncedTextValue = useDebounce(textValue, 150);
    useEffect(() => {
        if (queryArr.length > 0) {
            const result = [];
            for (let i = 0; i < queryArr.length; i += 3) {
                const block = queryArr.slice(i, i + 3).join(' ');
                result.push(block);
            }
            setDisplayArr(result);
        }
    }, [queryArr]);

    useEffect(() => {
        // Place your fuzzy search/filtering logic here, using debouncedTextValue
        // instead of textValue.
        const inputWords = debouncedTextValue.trim().toLowerCase().split(/\s+/);
        const filtered = filterList.filter((item: FilterItem) => {
            const name = item.name.toLowerCase();
            const key = item.key.toLowerCase();
            return inputWords.every(word => name.includes(word) || key.includes(word));
        });
        setFilteredSuggestions(filtered);
        setTextValue(debouncedTextValue);
    }, [debouncedTextValue, filterList]);

    useEffect(() => {
        if (displayArray && displayArray.length > 0) {
            // Convert display array items into queryArr format
            const newQueryArr: string[] = [];
            displayArray.forEach(item => {
                const parts = item.split(' ');
                newQueryArr.push(...parts);
            });

            let currentobjectArr: any[] = [];


            displayArray.forEach(item => {
                const parts = item.split(' ').map(part => part.trim());
                if (parts.length >= 3) {
                    currentobjectArr.push(searchFilterObject(parts[0]));
                }
            });
            setActiveObjArr(currentobjectArr);
            setQueryArr(newQueryArr);
        }
    }, [displayArray]);

    const searchFilterObject = (searchKey: string): FilterItem | undefined => {

        let filterObject = filterList.find(filter => filter.key.toLowerCase() === searchKey.toLowerCase());

        if (filterObject) {
            return filterObject;
        }
        return filterList.find(filter => filter.name.toLowerCase() === searchKey.toLowerCase());
    };


    useEffect(() => {
        const result = [];
        for (let i = 0; i < queryArr.length; i += 3) {
            const block = queryArr.slice(i, i + 3).join(' ');
            result.push(block);
        }

        setDisplayArr(result);

    }, [queryArr]);

    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (!showSuggestions) return;

        switch (e.key) {
            case "ArrowDown":
                
                if (Number.isInteger(currentStep)) {
                    setActiveSuggestion((prev) => (prev < filteredSuggestions.length - 1 ? prev + 1 : 0));
                } else {
                    if (currentStep.toString().split('.')[1] === '1') {
                        setActiveSuggestion((prev) => (prev < operatorArray.length - 1 ? prev + 1 : 0));
                    }
                    if (currentStep.toString().split('.')[1] === '2') {
                        setActiveSuggestion((prev) => (prev < valuesArray.length - 1 ? prev + 1 : 0));
                    }

                }
                break;
            case "ArrowUp":
                if (Number.isInteger(currentStep)) {
                    setActiveSuggestion((prev) => (prev > 0 ? prev - 1 : filteredSuggestions.length - 1));
                } else {
                    if (currentStep.toString().split('.')[1] === '1') {
                        setActiveSuggestion((prev) => (prev > 0 ? prev - 1 : operatorArray.length - 1));
                    }
                    if (currentStep.toString().split('.')[1] === '2') {
                        setActiveSuggestion((prev) => (prev > 0 ? prev - 1 : valuesArray.length - 1));
                    }
                }
                break;
            // case " ":
            //     if (currentStep.toString().split('.')[1] === '2' && !activeObjArr[activeObjArr.length - 1].isText) {
            //         if (Number.isInteger(currentStep)) {
            //             if (filteredSuggestions.length) {
            //                 const selectedSuggestion = filteredSuggestions[activeSuggestion];
            //                 if (queryArr.length > 0 && Number.isInteger(currentStep)) {
                               
            //                     if (isHomePage) {
            //                         if (activeSuggestion === -1) {
            //                             const encodedQuery = encodeURIComponent(displayArr.join('and'));
            //                             navigate(`/screener?query=${encodedQuery}`);
            //                         } else {
            //                             handleSuggestionClick(selectedSuggestion);
            //                             setActiveSuggestion(0);
            //                             setFilteredSuggestions(filterList);
            //                             setShowSuggestions(true);
            //                             inputRef.current && inputRef.current.focus();
            //                         }
            //                     } else {
            //                         if (activeSuggestion === -1) {
            //                             //screener api call function
            //                             companyScreenerApi(queryArr, 10, 0, "market_cap", "desc");
            //                             resetCurrentPage();
            //                             setShowSuggestions(false);

            //                         } else {

            //                             handleSuggestionClick(selectedSuggestion);
            //                             setActiveSuggestion(0);
            //                             setFilteredSuggestions(filterList);
            //                             setShowSuggestions(true);
            //                             inputRef.current && inputRef.current.focus();
            //                         }

            //                     }

            //                 } else {
            //                     handleSuggestionClick(selectedSuggestion);
            //                     setActiveSuggestion(0);
            //                     setFilteredSuggestions(filterList);
            //                     setShowSuggestions(true);
            //                     inputRef.current && inputRef.current.focus();
            //                 }
            //             }
            //         } else {
            //             if (currentStep.toString().split('.')[1] === '1') {
            //                 const selectedSuggestion = operatorArray[activeSuggestion];

            //                 handleSuggestionClick(selectedSuggestion);
            //                 setActiveSuggestion(0);
            //                 inputRef.current && inputRef.current.focus();
            //             }
                        
            //             if (currentStep.toString().split('.')[1] === '2' && activeObjArr[activeObjArr.length - 1].isText) {

            //                 console.log('currentStep.toString()');

            //                 if (activeObjArr[activeObjArr.length - 1].values.length === 1) {
            //                     //the user should enter the value in the input box like number_of_employees= 3000

            //                     if (textValue.match(/^\d/)) {
            //                         const suggestion = valuesArray[activeSuggestion];
            //                         const selectedSuggestion = textValue + suggestion;
            //                         handleSuggestionClick(selectedSuggestion);
            //                     } else {
            //                         return;
            //                     }

            //                     inputRef.current && inputRef.current.focus();
            //                 } else {
            //                     //the user should enter the value in the input box like market_cap= 30B

            //                     if (textValue.match(/^\d/)) {
            //                         const suggestion = valuesArray[activeSuggestion];
            //                         handleSuggestionClick(textValue + suggestion);
            //                     } else {
            //                         return;
            //                     }
            //                 }
            //             }
            //         }
            //     } else {
            //         e.preventDefault();
            //         if (Number.isInteger(currentStep)) {
            //             if (filteredSuggestions.length) {
            //                 const selectedSuggestion = filteredSuggestions[activeSuggestion];
            //                 if (queryArr.length > 0 && Number.isInteger(currentStep)) {
                              
            //                     if (isHomePage) {
                                   
            //                         if (activeSuggestion === -1) {
            //                             console.log('enter in text area func comp');

            //                             const encodedQuery = encodeURIComponent(displayArr.join('and'));
            //                             navigate(`/screener?query=${encodedQuery}`);
            //                         } else {
            //                             handleSuggestionClick(selectedSuggestion);
            //                             setActiveSuggestion(0);
            //                             setFilteredSuggestions(filterList);
            //                             setShowSuggestions(true);
            //                             inputRef.current && inputRef.current.focus();
            //                         }
            //                     } else {
            //                         if (activeSuggestion === -1) {
            //                             //screener api call function
            //                             companyScreenerApi(queryArr, 10, 0, "market_cap", "desc");
            //                             resetCurrentPage();
            //                             setShowSuggestions(false);

            //                         } else {

            //                             handleSuggestionClick(selectedSuggestion);
            //                             setActiveSuggestion(0);
            //                             setFilteredSuggestions(filterList);
            //                             setShowSuggestions(true);
            //                             inputRef.current && inputRef.current.focus();
            //                         }

            //                     }

            //                 } else {
                               
            //                     handleSuggestionClick(selectedSuggestion);
            //                     setActiveSuggestion(0);
            //                     setFilteredSuggestions(filterList);
            //                     setShowSuggestions(true);
            //                     inputRef.current && inputRef.current.focus();
            //                 }
            //             }
            //         } else {
            //             if (currentStep.toString().split('.')[1] === '1') {
            //                 const selectedSuggestion = operatorArray[activeSuggestion];
            //                 handleSuggestionClick(selectedSuggestion);
            //                 setActiveSuggestion(0);
            //                 inputRef.current && inputRef.current.focus();
            //             }
                      
            //             if (currentStep.toString().split('.')[1] === '2' && activeObjArr[activeObjArr.length - 1].isText) {
            //                 if (activeObjArr[activeObjArr.length - 1].values.length === 1) {
            //                     //the user should enter the value in the input box like number_of_employees= 3000

            //                     if (textValue.match(/^\d/)) {
            //                         const suggestion = valuesArray[activeSuggestion];
            //                         const selectedSuggestion = textValue + suggestion;
            //                         handleSuggestionClick(selectedSuggestion);
            //                     } else {
            //                         return;
            //                     }

            //                     inputRef.current && inputRef.current.focus();
            //                 } else {
            //                     //the user should enter the value in the input box like market_cap= 30B

            //                     if (textValue.match(/^\d/)) {
            //                         const suggestion = valuesArray[activeSuggestion];
            //                         handleSuggestionClick(textValue + suggestion);
            //                     } else {
            //                         return;
            //                     }
            //                 }
            //             }
            //         }
            //     }
            //     break;
            case "Enter":

                e.preventDefault();

                if (Number.isInteger(currentStep)) {
                    if (filteredSuggestions.length) {
                        const selectedSuggestion = filteredSuggestions[activeSuggestion];
                        if (queryArr.length > 0 && Number.isInteger(currentStep)) {
                            if (activeSuggestion === -1) {
                                // api call function

                                const encodedQuery = encodeURIComponent(displayArr.join('and'));
                                onModalClose();
                                navigate(`/screener?query=${encodedQuery}`);
                                setShowSuggestions(false);

                            } else {

                                handleSuggestionClick(selectedSuggestion);
                                setActiveSuggestion(0);
                                setFilteredSuggestions(filterList);
                                setShowSuggestions(true);
                                inputRef.current && inputRef.current.focus();
                            }

                        } else {
                            handleSuggestionClick(selectedSuggestion);
                            setActiveSuggestion(0);
                            setFilteredSuggestions(filterList);
                            setShowSuggestions(true);
                            inputRef.current && inputRef.current.focus();
                        }
                    }
                } else {
                    if (currentStep.toString().split('.')[1] === '1') {
                        
                        const selectedSuggestion = operatorArray[activeSuggestion];
                        handleSuggestionClick(selectedSuggestion);
                        setActiveSuggestion(0);
                        inputRef.current && inputRef.current.focus();
                    }
                    if (currentStep.toString().split('.')[1] === '2' && !activeObjArr[activeObjArr.length - 1].isText) {
                        const selectedSuggestion = valuesArray[activeSuggestion];
                        handleSuggestionClick(selectedSuggestion.value);
                        setActiveSuggestion(-1);
                        inputRef.current && inputRef.current.focus();
                    }
                    if (currentStep.toString().split('.')[1] === '2' && activeObjArr[activeObjArr.length - 1].isText) {


                        if (activeObjArr[activeObjArr.length - 1].values.length === 1) {
                            //the user should enter the value in the input box like number_of_employees= 3000
                            if (textValue.match(/^\d/)) {
                                const suggestion = valuesArray[activeSuggestion];
                                const selectedSuggestion = textValue + suggestion;
                                handleSuggestionClick(selectedSuggestion);
                            } else {
                                return;
                            }
                            inputRef.current && inputRef.current.focus();
                        } else {
                            //the user should enter the value in the input box like market_cap= 30B

                            if (textValue.match(/^\d/)) {
                                const suggestion = valuesArray[activeSuggestion];
                                handleSuggestionClick(textValue + suggestion);
                            } else {
                                return;
                            }
                        }
                    }
                }

                break;

            case "Backspace":
                e.preventDefault();

                if (currentStep >= 1 || textValue.length <= 0) {

                    if (queryArr.includes(textValue.length > 0 ? textValue : queryArr.slice(-1).join(""))) {
                     
                        let searchValue = queryArr.slice(0, queryArr.length - 1).join(' ');
                        let splitArr = queryArr.slice(0, queryArr.length - 1);
                        setQueryArr(splitArr);
                        setQueryOriginalArr(splitArr);
                        setsearchTerm(searchValue);

                        setCurrentStep((prevState) => {
                            if (Number.isInteger(prevState)) {

                                // If the previous state is an integer, subtract 0.7 from it
                                // alert(`${prevState},${currentStep}`);
                                // let valuesArrayList = activeObjArr[parseInt((currentStep).toString().split('.')[0]) - 2]["filter_type"] === "big_number" ? [...activeObjArr[parseInt((currentStep).toString().split('.')[0]) - 2]["values"], ...bigIntFilters] : [...activeObjArr[parseInt((currentStep).toString().split('.')[0]) - 2]["values"];
                                //  let valuesArrayList = activeObjArr[activeObjArr.length - 1]["filter_type"] === "big_number" ? [...activeObjArr[activeObjArr.length - 1]["values"], ...bigIntFilters] : [...activeObjArr[activeObjArr.length - 1]["values"]];
                                let valuesArrayList = activeObjArr[activeObjArr.length - 1]["values"];
                                let valuesArray = activeObjArr.length > 0 ? valuesArrayList : [];
                                let operatorArray = activeObjArr.length > 0 ? activeObjArr[activeObjArr.length - 1]["operatorSuggestions"] : [];
                                setValuesArray(valuesArray);
                                setOperatorArray(operatorArray);
                                setActiveSuggestion(0);
                                return parseFloat((prevState - 0.8).toFixed(1)); // Convert to float and fix to one decimal place

                            } else {
                                // If the previous state is already a float, subtract 0.1 from it

                                if (prevState.toString().split('.')[1] === "1") {
                                  
                                    let resultArr = activeObjArr.slice(0, -1);

                                    let valuesArray = activeObjArr[activeObjArr.length - 1]["values"];
                                    let operatorArray = activeObjArr[activeObjArr.length - 1]["operatorSuggestions"];
                                    setValuesArray(valuesArray);
                                    setOperatorArray(operatorArray);
                                    setActiveObjArr(resultArr);
                                }
                                if (prevState.toString().split('.')[1] === "2") {
                                   
                                    let valuesArray = activeObjArr[activeObjArr.length - 1]["values"];
                                    let operatorArray = activeObjArr[activeObjArr.length - 1]["operatorSuggestions"];
                                    setValuesArray(valuesArray);
                                    setOperatorArray(operatorArray);
                                    setActiveSuggestion(0);
                                }
                                if (prevState.toString().split('.')[1] === "3") {
                                   
                                    let valuesArray = activeObjArr.length > 0 ? activeObjArr[activeObjArr.length - 1]["values"] : [];
                                    let operatorArray = activeObjArr[activeObjArr.length - 1]["operatorSuggestions"];
                                    
                                    setValuesArray(valuesArray);
                                    setOperatorArray(operatorArray);
                                    setActiveSuggestion(0);
                                }
                                return parseFloat((prevState - 0.1).toFixed(1)); // Convert to float and fix to one decimal place
                            }
                        });
                    } else {
                    
                        // if (Number.isInteger(currentStep)) {
                        //     setFilteredSuggestions(filterList);
                        // }
                        let newSearchValue = searchTerm.split('').slice(0, -1).join('');
                        setsearchTerm(newSearchValue);
                        let newSearchTextValue = textValue.split('').slice(0, -1).join('');
                        setTextValue(newSearchTextValue);
                        let newSearchInputTextValue = inputTextValue.split('').slice(0, -1).join('');
                        setInputTextValue(newSearchInputTextValue);
                        let operatorArray = activeObjArr.length > 0 ? activeObjArr[activeObjArr.length - 1]["operatorSuggestions"] : [];
                        setOperatorArray(operatorArray);
                        // let valuesArrayList = newSearchValue === "" ? [] : (/^\d/.test(newSearchValue) ? activeObjArr[activeObjArr.length - 1]["values"] : [...activeObjArr[activeObjArr.length - 1]["values"], ...bigIntFilters]);
                        let valuesArrayList;
                        if (newSearchValue === "") {
                            return;
                            } else {
                          
                            if (activeObjArr.length > 0) {
                               valuesArrayList = activeObjArr[activeObjArr.length - 1]["values"];
                            }
                        }

                        let valuesArray = activeObjArr.length > 0 ? valuesArrayList : [];
                        setValuesArray(valuesArray);

                        setShowSuggestions(true);
                        setActiveSuggestion(0);
                    }
                } else {
                   
                    if (Number.isInteger(currentStep)) {
                        setFilteredSuggestions(filterList);
                    }
                    let newSearchValue = searchTerm.split('').slice(0, -1).join('');
                    setsearchTerm(newSearchValue);
                    let newSearchTextValue = textValue.split('').slice(0, -1).join('');
                    setTextValue(newSearchTextValue);
                    let newSearchInputTextValue = inputTextValue.split('').slice(0, -1).join('');
                    setInputTextValue(newSearchInputTextValue);
                    let valuesArray = activeObjArr.length > 0 ? activeObjArr[activeObjArr.length - 1]["values"] : [];
                    setValuesArray(valuesArray);
                    let operatorArray = activeObjArr.length > 0 ? activeObjArr[activeObjArr.length - 1]["operatorSuggestions"] : [];
                    setOperatorArray(operatorArray);

                    setShowSuggestions(true);
                    setActiveSuggestion(0);
                }
                break;
            default:
                break;
        }
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
                setShowSuggestions(false);
                setActiveSuggestion(-1);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);


    useEffect(() => {
        if (activeSuggestion >= 0 && suggestionsRef.current) {
            const suggestionsList = suggestionsRef.current.querySelector('.suggestions-list');
            const selectedElement = suggestionsList?.children[activeSuggestion] as HTMLElement;

            if (selectedElement && suggestionsList) {
                const containerHeight = suggestionsList.clientHeight;
                const scrollTop = suggestionsList.scrollTop;
                const elementTop = selectedElement.offsetTop;
                const elementHeight = selectedElement.offsetHeight;

                // Scroll down if element is below viewport
                if (elementTop + elementHeight > scrollTop + containerHeight) {
                    suggestionsList.scrollTop = elementTop + elementHeight - containerHeight;
                }
                // Scroll up if element is above viewport
                else if (elementTop < scrollTop) {
                    suggestionsList.scrollTop = elementTop;
                }
            }
        }
    }, [activeSuggestion]);

    const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {

        event.preventDefault();
        const inputValue = event.target.value;
        if (Number.isInteger(currentStep)) {
            // Multi-word fuzzy search for filter suggestions (name and key)
            const inputWords = inputValue.trim().toLowerCase().split(/\s+/);
            const filtered = filterList.filter((item: FilterItem) => {
                const name = item.name.toLowerCase();
                const key = item.key.toLowerCase();
                return inputWords.every(word => name.includes(word) || key.includes(word));
            });
            setFilteredSuggestions(filtered);
            setTextValue(inputValue);
        } else {
            // if the current step is 1.1
            if (currentStep.toString().split('.')[1] === '1') {
              
                let operatorArray = activeObjArr[activeObjArr.length - 1]["operatorSuggestions"];
                const filtered = operatorArray.filter((item: any) =>
                    item.toLowerCase().includes(inputValue.toLowerCase())
                );
                setOperatorArray(filtered);

                let operators = [">", "<", ">=", "<=", "="];
                if (operators.includes(inputValue) == false) {
                    setOperatorArray(operatorArray);
                    return
                }

            }
            if (currentStep.toString().split('.')[1] === '2') {

                let istextValue = activeObjArr[activeObjArr.length - 1].type;
                switch (istextValue) {
                    case "int":

                        //  let intAlphabetRegex = /^[0-9a-zA-Z_]+$/; //regex to accept integers,alphabets and underscore
                        let intAlphabetRegex = /^[0-9]+$/;
                        if (intAlphabetRegex.test(inputValue)) {
                           
                            if (inputValue.match(/^\d/)) {
                                // logic if the first character is a number
                                let valueArray = activeObjArr[activeObjArr.length - 1]["values"];

                                setValuesArray(valueArray);
                            } else {
                               
                                let valueArray = [...bigIntFilters];
                                const filtered = valueArray.filter(item =>
                                    item.toLowerCase().includes(inputValue.toLowerCase())
                                );
                                setValuesArray(filtered);
                            }
                        } else {

                            return;
                        }
                        break;
                    case "float":

                        const regex = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/;
                        if (regex.test(inputValue)) {
                           
                            let valueArray = activeObjArr[activeObjArr.length - 1]["values"];

                            setValuesArray(valueArray);

                        } else {
                           
                            return;
                        }
                        break;
                    default:
                        // default case for the filter type is string
                      
                        let valueArray = activeObjArr[activeObjArr.length - 1]["values"];
                        const filtered = valueArray.filter((item: any) =>
                            item.name.toLowerCase().includes(inputValue.toLowerCase())
                        );
                        setValuesArray(filtered);

                }
            }
        }
        setInputTextValue(inputValue);
        setTextValue(inputValue);
        setActiveSuggestion(0);
        setsearchTerm(inputValue);
        setShowSuggestions(true);
    }


    const handleSuggestionClick = (suggestItem: any) => {
       
        setTextValue("");
        // if the current step is integer, then we are in the first step like 1
        if (Number.isInteger(currentStep)) {

            //  queryArr = ['industry', '=', 'medical_distribution']
            setQueryArr((prevArr) => {
                const result = [...prevArr, suggestItem.key];
                setsearchTerm(result.join(' '));
                return result;
            });

            setQueryOriginalArr((prevArr) => {
                const result = [...prevArr, suggestItem.name];
                return result;
            });
            // Increment step by 0.1
            setCurrentStep((prevState) => parseFloat((prevState + 0.1).toFixed(1)));
            setActiveObjArr((prevArr) => [...prevArr, suggestItem]);
            let operatorArrayList = suggestItem["operatorSuggestions"];
            setOperatorArray(operatorArrayList);
            setFilteredSuggestions(filterList);
            inputRef.current && inputRef.current.focus();
        }
        else if (currentStep.toString().split('.')[1] === '1') {
            // if the current step is 1.1
            if (activeObjArr[activeObjArr.length - 1]["filter_type"] === "big_number") {

                if (suggestItem.length >= 1) {

                    const cleanedSuggestItem = suggestItem.replace(/\(Millons\)|\(Billions\)|\(Trillions\)/g, '').trim();
                    setQueryArr((prevArr) => {
                        const result = [...prevArr, cleanedSuggestItem];
                        setsearchTerm(result.join(' '));
                        return result;
                    });
                    setQueryOriginalArr((prevArr) => {
                        const result = [...prevArr, cleanedSuggestItem];
                        return result;
                    });
                    setCurrentStep((prevState) => parseFloat((prevState + 0.1).toFixed(1)));
                } else {
                    return;
                }
            } else {
                setQueryArr((prevArr) => {
                    const result = [...prevArr, suggestItem];
                    setsearchTerm(result.join(' '));
                    return result;
                });
                setQueryOriginalArr((prevArr) => {
                    const result = [...prevArr, suggestItem];
                    return result;
                });
                setCurrentStep((prevState) => parseFloat((prevState + 0.1).toFixed(1)));

            }

            if (activeObjArr[activeObjArr.length - 1]["filter_type"] === 'big_number') {

                let valuesArray = activeObjArr[activeObjArr.length - 1]["values"];
                setValuesArray(valuesArray);
            } else {
              
                let valuesArray = [...activeObjArr[activeObjArr.length - 1]["values"]];
              
                setValuesArray(valuesArray);
            }
            inputRef.current && inputRef.current.focus();
        } else {

            setQueryArr((prevArr) => {
                const result = [...prevArr, suggestItem];
                setsearchTerm(result.join(' '));
                return result;
            });
            setQueryOriginalArr((prevArr) => {
                const result = [...prevArr, suggestItem];
                return result;
            });
            setCurrentStep((prevState) => {
                // If the previous state is 3.4, increment it to 4
                let result = prevState.toString().split('.')[0];
                return parseFloat(result) + 1;
            });
            setActiveSuggestion(-1);
            inputRef.current && inputRef.current.focus();
        }
        setInputTextValue('');
    }

    const onRemoveItemClick = (item: string, index: number) => {
       
        // Implement the logic to remove the badge or clear the text
        let start = index == 0 ? 0 : (index * 3);

        setQueryOriginalArr(prevQueryArr => {
            const newQueryArr = [...prevQueryArr];
            newQueryArr.splice(start, 3);
            return newQueryArr;
        });

        setQueryArr(prevQueryArr => {
            const newQueryArr = [...prevQueryArr];
            newQueryArr.splice(start, 3);
            return newQueryArr;
        });

        setCurrentStep((prevState) => {
            // If the previous state is 3.4, increment it to 4
            let result = prevState.toString().split('.')[0];

            return parseFloat(result) - 1;
        });
        setActiveObjArr(activeObjArr);
    };



    const onQuerySubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();

        let encodedQuery = encodeURIComponent(displayArr.join('and'));
        window.location.href = `/screener?query=${encodedQuery}`;
    };

    const onViewExample = (query: string) => {

        if (isHomePage) {

            navigate(`/screener?query=${query}`);
        } else {
            setTextValue('');
            let queryList = query.split(' ');
            let dataArr: string[] = [];
            const filtered = filterList.filter((item: any) =>
                item.key.toLowerCase() === queryList[0].toLowerCase()
            );

            if (filtered.length > 0 && filtered[0].type === 'int' || filtered.length > 0 && filtered[0].type === 'float') {
                setQueryOriginalArr(queryList);
                dataArr = queryList
            } else {
                let valueList = filtered[0]["values"];
                if (valueList.length > 0) {

                    let dataValue: any[] = []
                    valueList.forEach((item: any, index: number) => {
                        if (item.value.toLowerCase() === queryList[2].toLowerCase()) {
                            dataValue.push(item);
                        }
                    });

                    dataArr = [queryList[0], queryList[1], dataValue[0].value];
                    setQueryOriginalArr([queryList[0], queryList[1], dataValue[0].value]);
                }


            }

            setActiveObjArr(filtered);
            setCurrentStep(2);
            setQueryArr(queryList);
            setsearchTerm(query);
            handleFilterDataApi(dataArr);
            setFiltersOpen(false);
            setShowSuggestions(false);
        }
    }

    const onShowPanelClick = (panelName: any, e: any) => {
        e.stopPropagation();
        setActiveEducationElementId(panelName);
        setShowSuggestions(false);
        setFiltersOpen(true);
    }

    const handleCloseFilters = () => {
        setFiltersOpen(false);
        setActiveEducationElementId(null);
    }

    const onQueryAppend = (filter: string[]) => {

        let newQueryArr = [...queryArr, ...filter];
        setQueryArr(newQueryArr);
        setQueryOriginalArr(newQueryArr);
        onScreenerQueryAppend(newQueryArr);
        setShowSuggestions(false);

    }

    const onTextAreaClick = (e: React.MouseEvent<HTMLTextAreaElement>) => {
        e.stopPropagation(); // Prevent the event from bubbling up to parent elements
        e.preventDefault();
        if (isDesktop) {
            setShowSuggestions(true);

        } else {
            if (isActive) {
                setShowSuggestions(true);
            } else {
                setShowSuggestions(false);
                setShowTextAreaModal(true);
            }

        }
    }

    const handleBoxClick = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        e.preventDefault();
        if (isDesktop) {
            inputRef.current && inputRef.current.focus();
            setShowSuggestions(true);
        } else {
            if (isActive) {
                inputRef.current && inputRef.current.focus();
                setShowSuggestions(true);
            } else {
                setShowSuggestions(false);
                setShowTextAreaModal(true);
            }
        }

    }

    useEffect(() => {
        if (isInputFocused && !isDesktop) {
            setShowSuggestions(true);
            setActiveSuggestion(-1);

            // Function to attempt focus
            const attemptFocus = () => {
                if (inputRef.current) {
                    // Force the keyboard to show on mobile
                    inputRef.current.focus();
                    inputRef.current.click();

                    // Set a small timeout to ensure the focus is maintained
                    setTimeout(() => {
                        if (document.activeElement !== inputRef.current) {
                            inputRef.current?.focus();
                        }
                    }, 50);

                    return true;
                }
                return false;
            };

            // Initial attempt with a small delay to ensure DOM is ready
            setTimeout(() => {
                if (!attemptFocus()) {
                    // If initial attempt fails, try again with a small delay
                    const focusInterval = setInterval(() => {
                        if (attemptFocus()) {
                            clearInterval(focusInterval);
                        }
                    }, 100);

                    // Clear interval after 2 seconds to prevent infinite attempts
                    setTimeout(() => clearInterval(focusInterval), 2000);
                }
            }, 100);
        }
    }, [isInputFocused, isDesktop]);


    return (
        <>
            <div className={`text-center justify-center lg:max-w-4xl mx-auto`}>
                <div className="relative p-2 max-w-auto md:max-w-2xl lg:max-w-3xl mx-auto border border-gray-300 rounded-lg shadow-sm" ref={suggestionsRef}>

                    <div className="w-full min-h-[80px] bg-white cursor-pointer" onClick={handleBoxClick}>

                        <div className="flex flex-wrap w-full">

                            {displayArr.map((item, index) => (
                                item.split(' ').length >= 3 ?
                                    (
                                        <div key={index} className="bg-background-dark text-xs font-semibold mr-2 mb-2 px-2 py-1 rounded-full text-black">
                                            {item}
                                            <button onClick={() => onRemoveItemClick(item, index)} className="ml-2 text-red-800 font-bold hover:text-red-600">
                                                &times;
                                            </button>
                                        </div>
                                    )
                                    : (
                                        <div key={index} className="text-black text-sm font-medium mb-2 py-1 rounded">
                                            {item}
                                        </div>
                                    )
                            ))}

                            <textarea
                                className="flex-1 p-1 border-none min-w-[100px] outline-none resize-none h-8 focus:outline-none focus:ring-0 text-black"
                                onClick={onTextAreaClick}
                                onKeyDown={handleKeyDown}
                                onChange={handleInputChange}
                                ref={inputRef}
                                placeholder={queryArr.length > 0 ? "" : "Set your own criteria..."}
                                value={textValue}
                            />
                        </div>
                    </div>


                    {(showSuggestions && filteredSuggestions.length > 0 && Number.isInteger(currentStep)) && (
                        <div className="absolute left-2 right-2 lg:left-4 lg:right-4 mt-0 bg-white rounded-lg shadow-lg border border-gray-200 z-10 max-h-[300px] overflow-y-scroll overflow-x-hidden [&::-webkit-scrollbar]:block [&::-webkit-scrollbar]:w-[5px] md:[&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-gray-100 suggestions-list">
                            {filteredSuggestions.map((suggestion, index) => (
                                <div
                                    role="button"
                                    tabIndex={0}
                                    key={index}
                                    className={`px-3 py-3 hover:bg-gray-100 cursor-pointer border-b last:border-b-0 ${activeSuggestion === index ? 'bg-gray-50' : ''
                                        }`}
                                    onClick={() => handleSuggestionClick(suggestion)}

                                >
                                    <div className="space-y-2 text-start">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <span className="text-xs font-semibold text-gray-900 me-3">{suggestion.name}</span>
                                                <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                                                    {suggestion.key}
                                                </span>
                                            </div>
                                            <button
                                                className="font-semibold transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 whitespace-nowrap border border-gray-800 rounded-3xl text-black bg-transparent hover:bg-gray-700 hover:text-white focus:ring-gray-700 px-2 py-1 text-[11px] lg:text-xs hidden md:block"

                                                onClick={(e) => onShowPanelClick(suggestion.glossary_key, e)}
                                            >
                                                Learn More
                                            </button>
                                            <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="w-4 h-4 md:hidden"
                                                onClick={(e) => onShowPanelClick(suggestion.glossary_key, e)}
                                            />
                                        </div>
                                        <p className="hidden md:inline-block text-xs text-gray-700 text-left">
                                            {suggestion.shortDescription}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Suggestions dropdown for second step*/}
                    {(currentStep.toString().split('.')[1] === '1' && showSuggestions) && (
                        <div className="absolute left-2 right-2 lg:left-4 lg:right-4 mt-0 bg-white rounded-lg shadow-lg border border-gray-200 z-10 max-h-[300px] overflow-y-scroll [&::-webkit-scrollbar]:block [&::-webkit-scrollbar]:w-[5px] md:[&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-gray-100 suggestions-list">
                            {operatorArray.map((suggestion, index) => (
                                <div
                                    key={index}
                                    className={`px-3 py-4 hover:bg-gray-100 cursor-pointer border-b last:border-b-0 ${activeSuggestion === index ? 'bg-gray-50' : ''
                                        }`}
                                    onClick={() => handleSuggestionClick(suggestion)}

                                >
                                    <div className="space-y-2 text-start">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <span className="text-sm font-semibold text-gray-900 me-3">{suggestion}</span>

                                            </div>
                                        </div>

                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Suggestions dropdown for third step and if the filter is text */}
                    {(currentStep.toString().split('.')[1] === '2' && showSuggestions && activeObjArr[activeObjArr.length - 1].isText) && (

                        activeObjArr[activeObjArr.length - 1].values.length === 1 ?
                            (
                                //the user should enter the value in the input box like number_of_employees= 3000
                                <div className="absolute left-2 right-2 lg:left-4 lg:right-4 mt-0 bg-white rounded-lg shadow-lg border border-gray-200 z-10 max-h-[300px] overflow-y-scroll [&::-webkit-scrollbar]:block [&::-webkit-scrollbar]:w-[5px] md:[&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-gray-100 suggestions-list">
                                    {valuesArray.map((suggestion, index) => (
                                        <div
                                            key={index}
                                            className={`px-3 py-4 hover:bg-gray-100 cursor-pointer border-b last:border-b-0 ${activeSuggestion === index ? 'bg-gray-50' : ''
                                                }`}
                                            onClick={() => handleSuggestionClick(textValue + suggestion)}

                                        >
                                            <div className="space-y-2 text-start">
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <span className="text-sm font-semibold text-gray-900 me-3">{textValue + suggestion}</span>
                                                        {/* <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                                                            {suggestion.value} 
                                                        </span> */}
                                                    </div>

                                                </div>

                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )
                            : (
                                //the user should enter the value in the input box like market_cap= 30B
                                <div className="absolute left-2 right-2 lg:left-4 lg:right-4 mt-0 bg-white rounded-lg shadow-lg border border-gray-200 z-10 max-h-[300px] overflow-y-scroll [&::-webkit-scrollbar]:block [&::-webkit-scrollbar]:w-[5px] md:[&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-gray-100 suggestions-list">
                                    {valuesArray.map((suggestion, index) => (
                                        <div
                                            key={index}
                                            className={`px-3 py-4 hover:bg-gray-100 cursor-pointer border-b last:border-b-0 ${activeSuggestion === index ? 'bg-gray-50' : ''
                                                }`}
                                            onClick={() => handleSuggestionClick(textValue.match(/^\d/) ? textValue + suggestion : suggestion)}

                                        >
                                            <div className="space-y-2 text-start">
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <span className="text-sm font-semibold text-gray-900 me-3">{textValue.match(/^\d/) ? textValue + suggestion : suggestion}</span>
                                                        {/* <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                                                            {suggestion.value} 
                                                        </span> */}
                                                    </div>

                                                </div>

                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )

                    )

                    }

                    {/* Suggestions dropdown for third step and if the filter is not text like industry  and exchange */}
                    {
                        (currentStep.toString().split('.')[1] === '2' && showSuggestions && !activeObjArr[activeObjArr.length - 1].isText) &&
                        (
                            <div className="absolute left-2 right-2 lg:left-4 lg:right-4 mt-0 bg-white rounded-lg shadow-lg border border-gray-200 z-10 max-h-[300px] overflow-y-scroll [&::-webkit-scrollbar]:block [&::-webkit-scrollbar]:w-[5px] md:[&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-gray-100 suggestions-list">
                                {valuesArray.map((suggestion, index) => (
                                    <div
                                        key={index}
                                        className={`px-3 py-4 hover:bg-gray-100 cursor-pointer border-b last:border-b-0 ${activeSuggestion === index ? 'bg-gray-50' : ''
                                            }`}
                                        onClick={() => handleSuggestionClick(suggestion.value)}

                                    >
                                        <div className="space-y-2 text-start">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <span className="text-sm font-semibold text-gray-900 me-3">{suggestion.name}</span>
                                                    <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                                                        {suggestion.value}
                                                    </span>
                                                </div>

                                            </div>

                                        </div>
                                    </div>
                                ))}
                            </div>
                        )
                    }

                    <div className="flex items-center justify-between text-sm mt-2">
                        <div className="flex items-center gap-2">
                            {/* <button
                                onClick={() => {
                                    setScreenerIdeasOpen(true);
                                    setActiveTab('screen');
                                }}
                                className="inline-flex items-center px-3 py-1 rounded-xl bg-white text-black hover:bg-black hover:text-white border border-gray-300 hover:border-black transition-colors duration-200 text-xs font-medium"
                                style={{ animationDelay: `1.25s` }}
                            >
                                Screen Ideas
                                <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="ms-2 w-3 h-3 mt-1" />
                            </button>
                            {
                                isAuthenticated &&
                                (<button
                                    onClick={() => {
                                        setScreenerIdeasOpen(true)
                                        setActiveTab('saved')
                                    }}
                                    className="inline-flex items-center px-3 py-1 rounded-xl bg-white text-black hover:bg-black hover:text-white border border-gray-300 hover:border-black transition-colors duration-200 text-xs font-medium"
                                >
                                    Saved Ideas
                                    <FontAwesomeIcon icon={faSave} className="ml-1.5 h-3 w-3 hidden md:block" />
                                </button>)
                            }
                            <button
                                onClick={() => setFiltersOpen(true)}
                                className="inline-flex items-center px-3 py-1 rounded-xl bg-white text-black hover:bg-black hover:text-white border border-gray-300 hover:border-black transition-colors duration-200 text-xs font-medium"
                            >
                                All filters
                                <FontAwesomeIcon icon={faSliders} className="ml-1.5 h-3 w-3 hidden md:block" />
                            </button> */}
                            
                            <Button
                                type="outline"
                                size="xs"
                                onClick={() => {
                                    setScreenerIdeasOpen(true);
                                    setActiveTab('screen');
                                }}
                                icon={<FontAwesomeIcon icon={faLightbulb} className="ml-1.5 h-3 w-3 hidden md:block" />}
                                iconPosition="left"
                                className="border-[1.5px]"
                            >
                                Screen Ideas
                            </Button>
                            <Button
                                type="outline"
                                size="xs"
                                onClick={() => setFiltersOpen(true)}
                                icon={<FontAwesomeIcon icon={faSliders} className="ml-1.5 h-3 w-3 hidden md:block" />}
                                iconPosition="left"
                                className="border-[1.5px]"
                            >
                                All filters
                            </Button>
                            {
                                isAuthenticated &&
                                (<Button
                                    type="outline"
                                    size="xs"
                                    onClick={() => {
                                        setScreenerIdeasOpen(true)
                                        setActiveTab('saved')
                                    }}
                                    icon={<FontAwesomeIcon icon={faSave} className="ml-1.5 h-3 w-3 hidden md:block" />}
                                    iconPosition="left"
                                    className="border-[1.5px]"
                                >
                                    Saved Ideas
                                </Button>)
                            }
                        </div>
                        <div>
                            <button
                                className={`transition-colors duration-300 top-12 absolute right-0 ${(queryArr.length > 0 && Number.isInteger(currentStep))
                                    ? 'text-green-700 hover:text-green-800'
                                    : 'text-gray-400 cursor-not-allowed'
                                    }`}
                                disabled={!(queryArr.length > 0 && Number.isInteger(currentStep))}
                                aria-label="Submit search"
                                // onClick={isHomePage ? () => navigate(`/screener?query=${searchTerm}`) : onQuerySubmit}
                                onClick={onQuerySubmit}
                            >
                                <ArrowRightCircleIcon className="w-10 h-10" />
                            </button>
                            
                        </div>
                    </div>
                </div>


            </div>
            <ScreenerIdeasOffcanvas
                isOpen={isScreenerIdeasOpen}
                onClose={() => setScreenerIdeasOpen(false)}
                isAuthenticated={isAuthenticated}
                displayArr={displayArr}
                isHomePage={isHomePage}
                setQuery={onQueryAppend}
                activeTabForModal={activeTab as 'screen' | 'saved'}
                userId={userId}
            />

            <FiltersOffcanvas
                isOpen={isFiltersOpen}
                onClose={handleCloseFilters}
                onExampleClick={onViewExample}
                activeEducationElementId={activeEducationElementId}
                filterButtonPlaceholder="Run Example"
                isScreenerComp={true}
                onQuerySubmit={() => (null)}
            />

        </>
    );
};

export default ModalTextAreaFunc;